'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ClipboardIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { languages, useHighlighter } from './useHighlighter';
export function HighlightedCode(_a) {
    var { language, children, className, prominent } = _a, props = __rest(_a, ["language", "children", "className", "prominent"]);
    const highlighter = useHighlighter();
    if (highlighter) {
        const html = highlighter.codeToHtml(children, {
            lang: language,
            themes: {
                light: 'css-variables',
                dark: 'css-variables-dark'
            },
            defaultColor: false,
            transformers: [
                {
                    root(node) {
                        if (node.children.length === 1 &&
                            node.children[0].type === 'element' &&
                            node.children[0].tagName === 'pre') {
                            const preNode = node.children[0];
                            if (preNode.children.length === 1 &&
                                preNode.children[0].type === 'element' &&
                                preNode.children[0].tagName === 'code') {
                                const codeNode = preNode.children[0];
                                node.children = codeNode.children;
                            }
                        }
                    }
                }
            ]
        });
        return (_jsx("code", Object.assign({ className: clsx(className, 'shiki', prominent ? 'prominent bg-zinc-900' : 'bg-zinc-50 dark:bg-zinc-900') }, props, { dangerouslySetInnerHTML: { __html: html } })));
    }
    else {
        return (_jsx("code", Object.assign({ className: clsx(className, prominent ? 'prominent bg-zinc-900' : 'bg-zinc-50 dark:bg-zinc-900') }, props, { children: children })));
    }
}
export function Code(_a) {
    var props = __rest(_a, []);
    if ('language' in props && props.language && languages.includes(props.language)) {
        return (_jsx(HighlightedCode, { language: props.language, prominent: props.prominent, children: props.children }));
    }
    else {
        return _jsx("code", Object.assign({}, props));
    }
}
function CopyButton({ code }) {
    let [copyCount, setCopyCount] = useState(0);
    let copied = copyCount > 0;
    useEffect(() => {
        if (copyCount > 0) {
            let timeout = setTimeout(() => setCopyCount(0), 1000);
            return () => {
                clearTimeout(timeout);
            };
        }
    }, [copyCount]);
    return (_jsxs("button", { type: "button", className: clsx('group/button absolute right-1.5 top-2 overflow-hidden rounded-full py-1 pl-2 pr-3 text-xs font-medium opacity-0 backdrop-blur transition focus:opacity-100 group-hover:opacity-100', copied
            ? 'bg-emerald-400/10 ring-1 ring-inset ring-emerald-400/20'
            : 'hover:bg-white/7.5 dark:bg-white/2.5 bg-white/5 dark:hover:bg-white/5'), onClick: () => {
            window.navigator.clipboard.writeText(code).then(() => {
                setCopyCount((count) => count + 1);
            });
        }, children: [_jsxs("span", { "aria-hidden": copied, className: clsx('pointer-events-none flex items-center gap-1.5 text-zinc-400 transition duration-300', copied && '-translate-y-1.5 opacity-0'), children: [_jsx(ClipboardIcon, { className: "h-3 w-3 fill-zinc-500/20 stroke-zinc-500 transition-colors group-hover/button:stroke-zinc-400" }), "Copy"] }), _jsx("span", { "aria-hidden": !copied, className: clsx('pointer-events-none absolute inset-0 flex items-center justify-center text-emerald-400 transition duration-300', !copied && 'translate-y-1.5 opacity-0'), children: "Copied!" })] }));
}
export function CodeListing(_a) {
    var { className, code, language, prominent } = _a, props = __rest(_a, ["className", "code", "language", "prominent"]);
    const header = 'header' in props && props.header ? (props.header) : props.title ? (_jsx("h3", { className: clsx([
            'flex items-center px-3 py-2 text-xs uppercase',
            prominent ? 'text-white' : 'text-zinc-950 dark:text-white'
        ]), children: props.title })) : null;
    return (_jsxs("div", { className: clsx(className, 'not-prose group overflow-hidden rounded-lg border border-zinc-950/10 dark:border-white/10', prominent ? 'bg-zinc-700' : 'bg-zinc-50 dark:bg-zinc-900'), children: [header && _jsx("div", { className: clsx(prominent ? 'bg-zinc-600' : 'bg-zinc-200 dark:bg-zinc-800'), children: header }), _jsxs("div", { className: "relative", children: [_jsxs("pre", { className: "overflow-x-auto p-3 text-xs leading-5", children: [language && (_jsx(Code, { language: language, prominent: prominent, children: code })), !language && _jsx(Code, { prominent: prominent, children: code })] }), _jsx(CopyButton, { code: code })] })] }));
}
